import React, { useEffect, useMemo, useRef, useState } from "react";
import { Plus, Search, Trash2, Edit2, Save, X, AlertTriangle, CheckCircle2, Loader2, Package, ShieldOff, FileBox, Store as StoreIcon, FolderOpen, HelpCircle, Download, Upload } from "lucide-react";
import { http, errMsg } from "@/lib/api";
import { toast } from "sonner";

/**
 * Settings → App Mappings
 *
 * Per-tenant database of custom rules that tell the migration resolver how
 * to reinstall apps Winget doesn't cover. CRUD over `/api/app-mappings`.
 *
 * The page is intentionally minimal — the heavy lifting (fuzzy matching,
 * skip-list, MSI/Store detection) lives server-side in
 * `services/app_resolver.py`. This UI is just a data-entry surface with
 * a built-in "Test against a sample app" panel so operators can sanity-
 * check a mapping before saving it.
 */

const INSTALLER_TYPES = [
    { value: "winget",   label: "Winget package",            icon: Package },
    { value: "exe",      label: "EXE installer (NAS/path)",  icon: FileBox },
    { value: "msi",      label: "MSI installer (NAS/path)",  icon: FileBox },
    { value: "store",    label: "Microsoft Store / Appx",    icon: StoreIcon },
    { value: "portable", label: "Portable / folder copy",    icon: FolderOpen },
    { value: "manual",   label: "Manual reinstall only",     icon: HelpCircle },
];

const EMPTY_FORM = {
    displayName: "", publisher: "", versionRule: "",
    wingetId: "", installerPath: "", nasPath: "",
    installerType: "winget", silentArgs: "",
    requiresLicense: false, requiresReboot: false, adminRequired: true,
    notes: "", confidence: 90,
};

export default function AppMappings() {
    const [rows, setRows] = useState([]);
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [editing, setEditing] = useState(null); // mapping id
    const [busy, setBusy] = useState(false);
    const [testInput, setTestInput] = useState({ name: "", publisher: "" });
    const [testResult, setTestResult] = useState(null);
    const [importing, setImporting] = useState(false);
    const [importSummary, setImportSummary] = useState(null);
    const fileInputRef = useRef(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await http.get("/app-mappings", { params: q ? { q } : {} });
            setRows(data.mappings || []);
        } catch (e) {
            toast.error(errMsg(e, "Failed to load mappings"));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

    const submit = async () => {
        if (!form.displayName.trim()) {
            toast.error("Display name is required");
            return;
        }
        setBusy(true);
        try {
            if (editing) {
                await http.patch(`/app-mappings/${editing}`, form);
                toast.success("Mapping updated");
            } else {
                await http.post("/app-mappings", form);
                toast.success("Mapping created");
            }
            setForm(EMPTY_FORM);
            setCreating(false);
            setEditing(null);
            await load();
        } catch (e) {
            toast.error(errMsg(e, "Save failed"));
        } finally {
            setBusy(false);
        }
    };

    const del = async (m) => {
        if (!window.confirm(`Delete mapping for "${m.displayName}"?`)) return;
        try {
            await http.delete(`/app-mappings/${m.id}`);
            toast.success("Deleted");
            await load();
        } catch (e) {
            toast.error(errMsg(e, "Delete failed"));
        }
    };

    const startEdit = (m) => {
        setEditing(m.id);
        setCreating(true);
        setForm({ ...EMPTY_FORM, ...m });
    };

    const cancelEdit = () => { setEditing(null); setCreating(false); setForm(EMPTY_FORM); };

    const exportCsv = async () => {
        try {
            // Use http instance with responseType blob so we get the raw CSV
            // bytes instead of parsed JSON. Stream attached as a Blob URL so
            // the browser triggers a clean download with the server's filename.
            const r = await http.get("/app-mappings/export.csv", { responseType: "blob" });
            const cd = r.headers?.["content-disposition"] || "";
            const m = cd.match(/filename="([^"]+)"/);
            const filename = m ? m[1] : `moovbytes-app-mappings-${Date.now()}.csv`;
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a");
            a.href = url; a.download = filename;
            document.body.appendChild(a); a.click(); a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
            toast.success("CSV exported");
        } catch (e) {
            toast.error("Export failed: " + errMsg(e));
        }
    };

    const onPickImportFile = () => fileInputRef.current?.click();

    const onImportFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        e.target.value = "";  // allow re-uploading the same file
        setImporting(true);
        setImportSummary(null);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await http.post("/app-mappings/import", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setImportSummary(data);
            toast.success(
                `Imported: ${data.created} created · ${data.skipped_duplicates} dupes · ${data.failed} failed`,
            );
            await load();
        } catch (err) {
            toast.error("Import failed: " + errMsg(err));
        } finally {
            setImporting(false);
        }
    };

    const runTest = async () => {
        if (!testInput.name.trim()) { toast.error("Enter an app name to test"); return; }
        try {
            const { data } = await http.post("/app-mappings/test", {
                app: { name: testInput.name, publisher: testInput.publisher },
                // Include the in-progress form so the operator can preview
                // BEFORE saving, useful for tuning fuzzy match thresholds.
                candidate_mapping: form.displayName ? form : undefined,
            });
            setTestResult(data.resolved);
        } catch (e) {
            toast.error(errMsg(e, "Test failed"));
        }
    };

    const filtered = useMemo(() => {
        if (!q) return rows;
        const needle = q.toLowerCase();
        return rows.filter(r =>
            (r.displayName || "").toLowerCase().includes(needle) ||
            (r.publisher || "").toLowerCase().includes(needle),
        );
    }, [rows, q]);

    return (
        <div className="space-y-6" data-testid="app-mappings-page">
            <header className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.28em] text-[#00C2FF]/80">
                        <Package size={12} /> Application Migration
                    </div>
                    <h1 className="mt-1 font-display text-3xl font-bold tracking-tight">App Mappings</h1>
                    <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
                        Custom rules that tell the migration resolver how to reinstall apps Winget
                        doesn&apos;t cover — point to a Winget ID, NAS installer, MSI, Store ID, or
                        mark as manual. Tenant-scoped.
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".csv,text/csv"
                        className="hidden"
                        onChange={onImportFile}
                        data-testid="app-mappings-import-input"
                    />
                    <button
                        type="button"
                        onClick={exportCsv}
                        data-testid="app-mappings-export-btn"
                        className="inline-flex items-center gap-1.5 rounded-md border border-zinc-700 bg-zinc-900/60 px-3 py-2 text-[11px] font-bold uppercase tracking-widest text-zinc-200 transition hover:bg-zinc-800"
                    >
                        <Download size={12} /> Export CSV
                    </button>
                    <button
                        type="button"
                        onClick={onPickImportFile}
                        disabled={importing}
                        data-testid="app-mappings-import-btn"
                        className="inline-flex items-center gap-1.5 rounded-md border border-zinc-700 bg-zinc-900/60 px-3 py-2 text-[11px] font-bold uppercase tracking-widest text-zinc-200 transition hover:bg-zinc-800 disabled:opacity-60"
                    >
                        {importing ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />} Import CSV
                    </button>
                    <button
                        type="button"
                        onClick={() => { setCreating(true); setEditing(null); setForm(EMPTY_FORM); }}
                        data-testid="app-mappings-add-btn"
                        className="inline-flex items-center gap-1.5 rounded-md bg-gradient-to-r from-[#004AAD] to-[#00C2FF] px-3.5 py-2 text-[11px] font-bold uppercase tracking-widest text-white shadow-lg shadow-[#00C2FF]/20 transition hover:brightness-110"
                    >
                        <Plus size={12} /> New mapping
                    </button>
                </div>
            </header>

            {importSummary && (
                <div className="rounded-md border border-sky-800/50 bg-sky-950/30 p-3 text-sm" data-testid="app-mappings-import-summary">
                    <div className="flex items-center justify-between">
                        <div className="font-semibold text-sky-200">
                            CSV import — {importSummary.total_processed} row(s) processed
                        </div>
                        <button onClick={() => setImportSummary(null)} className="text-zinc-500 hover:text-zinc-200" data-testid="import-summary-close">
                            <X size={14} />
                        </button>
                    </div>
                    <div className="mt-1 flex flex-wrap gap-3 text-xs text-zinc-300">
                        <span className="text-emerald-300">{importSummary.created} created</span>
                        <span className="text-amber-300">{importSummary.skipped_duplicates} duplicates skipped</span>
                        <span className="text-rose-300">{importSummary.failed} failed</span>
                    </div>
                    {importSummary.failed_rows?.length > 0 && (
                        <details className="mt-2 text-xs">
                            <summary className="cursor-pointer text-rose-300">{importSummary.failed_rows.length} failed row(s)</summary>
                            <ul className="mt-1 ml-4 list-disc text-zinc-400">
                                {importSummary.failed_rows.slice(0, 20).map((f, i) => (
                                    <li key={i}>Row {f.row}: {f.error}</li>
                                ))}
                            </ul>
                        </details>
                    )}
                </div>
            )}

            {creating && (
                <MappingForm
                    form={form}
                    setForm={setForm}
                    onCancel={cancelEdit}
                    onSubmit={submit}
                    busy={busy}
                    editing={!!editing}
                    testInput={testInput}
                    setTestInput={setTestInput}
                    onTest={runTest}
                    testResult={testResult}
                />
            )}

            {/* Search */}
            <div className="flex items-center gap-2">
                <div className="relative max-w-md flex-1">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                        type="text" value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Filter by name or publisher…"
                        data-testid="app-mappings-search"
                        className="w-full rounded-md border border-border bg-background pl-9 pr-3 py-2 text-sm focus:border-[#00C2FF] focus:outline-none"
                    />
                </div>
                <button
                    type="button"
                    onClick={load}
                    className="rounded-md border border-border px-3 py-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground hover:bg-muted"
                >
                    Refresh
                </button>
            </div>

            {/* Table */}
            <div className="rounded-xl border border-border bg-surface" data-testid="app-mappings-table">
                {loading ? (
                    <div className="flex items-center justify-center gap-2 py-12 text-sm text-muted-foreground">
                        <Loader2 size={14} className="animate-spin" /> Loading…
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="flex flex-col items-center justify-center gap-2 px-6 py-12 text-center">
                        <ShieldOff size={24} className="text-muted-foreground/40" />
                        <div className="text-sm font-semibold">No mappings yet</div>
                        <div className="max-w-md text-xs text-muted-foreground">
                            Click <b>New mapping</b> above to add your first rule. The migration resolver
                            will start using it on the next inventory load.
                        </div>
                    </div>
                ) : (
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-border text-left font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                                <th className="px-4 py-2.5">App</th>
                                <th className="px-4 py-2.5">Publisher</th>
                                <th className="px-4 py-2.5">Type</th>
                                <th className="px-4 py-2.5">Target</th>
                                <th className="px-4 py-2.5 text-right">Confidence</th>
                                <th className="px-4 py-2.5"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map(m => (
                                <tr key={m.id} className="border-b border-border/50 last:border-b-0 hover:bg-muted/30" data-testid={`app-mapping-row-${m.id}`}>
                                    <td className="px-4 py-3">
                                        <div className="font-semibold">{m.displayName}</div>
                                        {m.versionRule && <div className="mt-0.5 font-mono text-[10px] text-muted-foreground">v{m.versionRule}</div>}
                                    </td>
                                    <td className="px-4 py-3 text-muted-foreground">{m.publisher || "—"}</td>
                                    <td className="px-4 py-3">
                                        <TypePill type={m.installerType} />
                                    </td>
                                    <td className="px-4 py-3 font-mono text-[11px] text-muted-foreground">
                                        {m.wingetId || m.installerPath || m.nasPath || "—"}
                                    </td>
                                    <td className="px-4 py-3 text-right font-mono text-[12px]">{m.confidence ?? 90}</td>
                                    <td className="px-4 py-3">
                                        <div className="flex items-center justify-end gap-1.5">
                                            <button
                                                type="button"
                                                onClick={() => startEdit(m)}
                                                title="Edit"
                                                data-testid={`app-mapping-edit-${m.id}`}
                                                className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                                            >
                                                <Edit2 size={13} />
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => del(m)}
                                                title="Delete"
                                                data-testid={`app-mapping-delete-${m.id}`}
                                                className="rounded-md p-1.5 text-muted-foreground hover:bg-red-500/10 hover:text-red-400"
                                            >
                                                <Trash2 size={13} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}

function TypePill({ type }) {
    const meta = INSTALLER_TYPES.find(t => t.value === type) || INSTALLER_TYPES[5];
    const Icon = meta.icon;
    return (
        <span className="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
            <Icon size={10} /> {type}
        </span>
    );
}

function MappingForm({ form, setForm, onCancel, onSubmit, busy, editing, testInput, setTestInput, onTest, testResult }) {
    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
    return (
        <div className="rounded-xl border border-[#00C2FF]/25 bg-gradient-to-br from-[#00C2FF]/5 to-transparent p-5" data-testid="app-mapping-form">
            <div className="mb-4 flex items-center justify-between">
                <h2 className="font-display text-lg font-bold">
                    {editing ? "Edit mapping" : "New mapping"}
                </h2>
                <button type="button" onClick={onCancel} className="text-muted-foreground hover:text-foreground">
                    <X size={16} />
                </button>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <FormField label="Display name *">
                    <input value={form.displayName} onChange={(e) => set("displayName", e.target.value)} data-testid="app-mapping-displayName" className={fieldCls} placeholder="e.g. Google Chrome" />
                </FormField>
                <FormField label="Publisher">
                    <input value={form.publisher || ""} onChange={(e) => set("publisher", e.target.value)} data-testid="app-mapping-publisher" className={fieldCls} placeholder="e.g. Google LLC" />
                </FormField>

                <FormField label="Installer type *">
                    <select value={form.installerType} onChange={(e) => set("installerType", e.target.value)} data-testid="app-mapping-installerType" className={fieldCls}>
                        {INSTALLER_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                </FormField>
                <FormField label="Version rule (optional)">
                    <input value={form.versionRule || ""} onChange={(e) => set("versionRule", e.target.value)} className={fieldCls} placeholder="e.g. >=2024" />
                </FormField>

                {form.installerType === "winget" && (
                    <FormField label="Winget ID *" full>
                        <input value={form.wingetId || ""} onChange={(e) => set("wingetId", e.target.value)} data-testid="app-mapping-wingetId" className={fieldCls} placeholder="e.g. Google.Chrome" />
                    </FormField>
                )}
                {(form.installerType === "exe" || form.installerType === "msi") && (
                    <>
                        <FormField label="Installer path / NAS share *" full>
                            <input value={form.installerPath || ""} onChange={(e) => set("installerPath", e.target.value)} data-testid="app-mapping-installerPath" className={fieldCls} placeholder="\\NAS\Installers\App\setup.exe" />
                        </FormField>
                        <FormField label="Silent args">
                            <input value={form.silentArgs || ""} onChange={(e) => set("silentArgs", e.target.value)} data-testid="app-mapping-silentArgs" className={fieldCls} placeholder="/quiet /norestart" />
                        </FormField>
                    </>
                )}

                <FormField label="Confidence (0-100)">
                    <input type="number" min={0} max={100} value={form.confidence}
                        onChange={(e) => set("confidence", Number(e.target.value))}
                        className={fieldCls} data-testid="app-mapping-confidence" />
                </FormField>

                <div className="col-span-1 sm:col-span-2 flex flex-wrap gap-4 pt-1">
                    <Toggle label="Requires license" value={form.requiresLicense} onChange={(v) => set("requiresLicense", v)} testId="app-mapping-requiresLicense" />
                    <Toggle label="Requires reboot" value={form.requiresReboot} onChange={(v) => set("requiresReboot", v)} testId="app-mapping-requiresReboot" />
                    <Toggle label="Admin required" value={form.adminRequired} onChange={(v) => set("adminRequired", v)} testId="app-mapping-adminRequired" />
                </div>

                <FormField label="Notes" full>
                    <textarea value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} rows={2} className={fieldCls} placeholder="e.g. Requires license activation after install" />
                </FormField>
            </div>

            {/* Test panel */}
            <div className="mt-5 rounded-md border border-dashed border-border bg-background/40 p-4" data-testid="app-mapping-test-panel">
                <div className="mb-2 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    <Search size={11} /> Test this mapping against a sample app
                </div>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                    <input value={testInput.name} onChange={(e) => setTestInput(t => ({ ...t, name: e.target.value }))} placeholder="App display name" className={fieldCls} data-testid="app-mapping-test-name" />
                    <input value={testInput.publisher} onChange={(e) => setTestInput(t => ({ ...t, publisher: e.target.value }))} placeholder="Publisher" className={fieldCls} data-testid="app-mapping-test-publisher" />
                    <button type="button" onClick={onTest} data-testid="app-mapping-test-run" className="rounded-md border border-border px-3 py-2 text-xs font-semibold uppercase tracking-widest hover:bg-muted">
                        Run resolver
                    </button>
                </div>
                {testResult && (
                    <div className="mt-3 rounded-md bg-black/20 p-3 text-[11px] font-mono" data-testid="app-mapping-test-result">
                        <div>category: <b className="text-[#7fe0ff]">{testResult.category}</b></div>
                        <div>confidence: <b>{testResult.confidence}</b></div>
                        <div>method: <b>{testResult.resolution?.method}</b></div>
                        {testResult.resolution?.reason && <div>reason: {testResult.resolution.reason}</div>}
                        {testResult.winget_id && <div>winget_id: {testResult.winget_id}</div>}
                    </div>
                )}
            </div>

            <div className="mt-5 flex items-center justify-end gap-2">
                <button type="button" onClick={onCancel} className="rounded-md border border-border px-3 py-2 text-xs font-semibold uppercase tracking-widest hover:bg-muted">
                    Cancel
                </button>
                <button type="button" onClick={onSubmit} disabled={busy} data-testid="app-mapping-save"
                    className="inline-flex items-center gap-1.5 rounded-md bg-gradient-to-r from-[#004AAD] to-[#00C2FF] px-4 py-2 text-[11px] font-bold uppercase tracking-widest text-white shadow-lg shadow-[#00C2FF]/20 transition hover:brightness-110 disabled:opacity-50">
                    {busy ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />}
                    {editing ? "Save changes" : "Create mapping"}
                </button>
            </div>
        </div>
    );
}

function FormField({ label, full = false, children }) {
    return (
        <label className={`block ${full ? "col-span-1 sm:col-span-2" : ""}`}>
            <span className="block text-[10px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">{label}</span>
            <div className="mt-1.5">{children}</div>
        </label>
    );
}

function Toggle({ label, value, onChange, testId }) {
    return (
        <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} data-testid={testId} className="accent-[#00C2FF]" />
            <span>{label}</span>
        </label>
    );
}

const fieldCls = "w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-[#00C2FF] focus:outline-none";
